import { createGuid } from '@bimser/core';
import { Drawer, DrawerProps } from 'antd';
import React, { createContext, useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import { DrawerManagerContextType, OpenDrawer } from './entities/IOpenDrawer';

const BCDrawerManagerContext = createContext<DrawerManagerContextType | null>(null);

export const BCDrawerManagerProvider: React.FC = ({ children }) => {
	const [drawers, setDrawers] = useState<{ id: string; props: DrawerProps }[]>([]);

	const update = (id: string, newProps: DrawerProps) => {
		setDrawers(prev =>
			prev.map(drawer =>
				drawer.id === id ? { ...drawer, props: { ...drawer.props, ...newProps } } : drawer
			)
		);
	};

	const close = (id: string) => {
		update(id, { open: false });
	};

	const destroy = (id: string) => {
		setDrawers(prev => prev.filter(drawer => drawer.id !== id));
	};

	const openDrawer: OpenDrawer = props => {
		const id = createGuid();
		setDrawers(prev => [...prev, { id, props }]);

		return {
			id,
			close: () => close(id),
			update: props => update(id, props),
			destroy: () => destroy(id)
		};
	};

	// Expose the method to the window object for micro apps
	React.useEffect(() => {
		if (!window.SynergyUI) {
			window.SynergyUI = {};
		}

		if (!window.SynergyUI.openDrawer) {
			window.SynergyUI.openDrawer = openDrawer;
		}
	}, []);

	return (
		<BCDrawerManagerContext.Provider value={{ openDrawer }}>
			{children}
			{ReactDOM.createPortal(
				drawers.map(({ id, props }) => (
					<Drawer
						key={id}
						open={props.open}
						onClose={e => {
							update(id, { open: false });
							props.onClose?.(e);
						}}
						afterOpenChange={open => {
							if (!open && props.destroyOnClose) {
								setDrawers(prev => prev.filter(drawer => drawer.id !== id));
							}
							props.afterOpenChange?.(open);
						}}
						{...props}
					/>
				)),
				document.body
			)}
		</BCDrawerManagerContext.Provider>
	);
};

export const useDrawerManager = () => {
	const context = useContext(BCDrawerManagerContext);
	if (!context) {
		throw new Error('useDrawerManager must be used within a DrawerManagerProvider');
	}
	return context;
};
